import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import * as DateTimeFunctions from '../../ts/DateTimeFunctions';
import GlobalState from '../../ts/globalStates/GlobalState';
import { IValuesCollection } from '../../ts/appTheme';
import { IDisplayValue } from '../../../../_common/platform/ts/Interfaces';
import { themeVars } from '../../../../pdc-platform/src/ts/themes/themeConstants';
import { applyWidgetCulture, ICultureValues } from '../../ts/Globalization';

import {
    AttributeSelectionType,
    IAttribute,
    IGlobalAttributeFilters,
    IAttributeOptions,
    IAttributeDataQuery,
    IGlobalAttributeFilter,
    IGlobalAttributeFilterCollection,
    IDataQueryData,
    IAttributeFilterWidgetParameters,
    IScopeOptions,
    INewAttributeFilterColections,
    ITreeDataQueryData,
    GetValuesResult,
    IQueryFilter,
    IGlobalFilterProperties,
    IAQLFilter
} from './Interfaces';

import { ATTRIBUTE_TYPES, ATTRIBUTE_CONTROL_TYPES } from '../../ts/ConfigurationConstants';

import {
    isFiscalTimePart,
    getControlAndAttributeTypes,
    applyModeIsOn,
    isKeySelect,
    isInRange,
    getTimePartValueRange,
    convertValuesToDisplayValues,
    isOfTimePartControlType,
    getDataQueryAttributes,
    isFullYearTimePart,
    normalizeDataQueryValue,
    IControlAndAttributeTypes,
    attributeCannotHaveValue,
    filterValueTypeCannotBeChanged,
    setIntervalsByControlType,
    getValidRelations,
    createAttributeID
} from './FilterUtilities';

import { AttributeValueSortingType } from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';
import { RELATIONS } from './Constants';
import { ITreeValues } from '../../../../pdc-platform/src/ts/Interfaces';

export function isOfEntityValuesControlType(controlType: string) {
    switch (controlType) {
        case ATTRIBUTE_CONTROL_TYPES.LIST:
        case ATTRIBUTE_CONTROL_TYPES.DROPDOWN:
        case ATTRIBUTE_CONTROL_TYPES.SLIDER:
        case ATTRIBUTE_CONTROL_TYPES.TREE:
            return true;

        default:
            return false;
    }
}

export function getDataQueryData(
    attributeFilter: IGlobalAttributeFilter,
    runTimeWidgetID: number,
    workspaceID: number,
    staticWidgetID: number,
    dataQuery?: IAttributeDataQuery
): {
    dataQueryData: IDataQueryData;
    keySelect: boolean;
    attributeDataQuery: IAttributeDataQuery;
} {
    const options = attributeFilter.options;
    const dataQueryData: IDataQueryData = {
        runTimeWidgetID: runTimeWidgetID,
        workspace: workspaceID,
        staticWidgetID: staticWidgetID
    };

    if (attributeFilter.expression != null) {
        dataQueryData.expression = attributeFilter.expression;
    } else if (attributeFilter.attribute != null) {
        dataQueryData.attribute = attributeFilter.attribute;
    }

    let attributeDataQuery: IAttributeDataQuery;
    let keySelect = isKeySelect(attributeFilter.controlType, options, dataQuery);

    if (options != null) {
        if (dataQuery === undefined) {
            dataQuery = options.dataQuery;
        }

        const dqAttributes = getDataQueryAttributes(dataQuery, attributeFilter, keySelect);

        dataQueryData.deepFiltering = options.deepFiltering;

        if (dqAttributes != null) {
            if (dqAttributes.attribute != null) {
                attributeDataQuery = dataQuery;
                dataQueryData.attribute = dqAttributes.attribute;
                delete dataQueryData.expression;
            } else if (dqAttributes.expression != null) {
                dataQueryData.expression = dqAttributes.expression;
                delete dataQueryData.attribute;
            }

            if (dqAttributes.keyAttribute != null) {
                dataQueryData.loadKeyAttribute = true;
                dataQueryData.keyAttribute = dqAttributes.keyAttribute;
            }

            keySelect = dqAttributes.keySelect;

            dataQueryData.limit = dataQuery.limit;
            dataQueryData.filters = dataQuery.filter;
            dataQueryData.globalFilterControl = dataQuery.globalFilterControl;
        }
    }

    return {
        dataQueryData: dataQueryData,
        keySelect: keySelect,
        attributeDataQuery: attributeDataQuery
    };
}

export function getDataQueryDataForTree(
    dataQueryData: IDataQueryData,
    attributeDataQuery: IAttributeDataQuery,
    options: IAttributeOptions
) {
    let rootNodeID: string;
    let parentAttribute: string;
    let labelAttribute: string;

    if (attributeDataQuery != null) {
        rootNodeID = attributeDataQuery.rootNode != null ? attributeDataQuery.rootNode.value : null;
        parentAttribute = attributeDataQuery.parentAttribute;
        labelAttribute = attributeDataQuery.labelAttribute;
    } else {
        rootNodeID = options.rootNode != null ? options.rootNode.value : null;
        parentAttribute = options.parentAttribute;
        labelAttribute = options.labelAttribute;
    }

    const result: ITreeDataQueryData = {
        ...dataQueryData,
        parentAttribute: parentAttribute,
        rootNodeID: rootNodeID,
        labelAttribute: labelAttribute
    };

    return result;
}

// Empty array is a correct value to make filters cleared by user be stored.
function correctFilterValue(
    values: IDisplayValue[],
    selectionType: string,
    controlType: string,
    attributeType: string,
    attributeOptions: IAttributeOptions
): IDisplayValue[] {
    if (values == null) {
        return null;
    }

    const keySelect = isKeySelect(controlType, attributeOptions);

    let result = convertValuesToDisplayValues(values, keySelect);

    if (result != null && result.length > 0) {
        result = result.filter((value) => value != null && value.value != null);

        if (result.length > 0) {
            if (selectionType !== 'multiply') {
                // In Single Mode use the first value only.
                result = [result[0]];
            }

            if (isOfTimePartControlType(controlType)) {
                const range = getTimePartValueRange(controlType);

                if (range != null) {
                    result = result.filter((value) => {
                        //return value != null && isInRange(value.value, range.start, range.end);
                        return isInRange(value.value, range.start, range.end);
                    });
                }
            } else if (DateTimeFunctions.isTypeOfDateOrDatetime(attributeType)) {
                result = result.filter((value) => {
                    //return value != null && value.value != null && !isNaN(Date.parse(value.value));
                    return !isNaN(Date.parse(value.value));
                });
            }
        }
    }

    return result;
}

export function getValidSelectionTypes(controlType: string): AttributeSelectionType[] {
    switch (controlType) {
        case ATTRIBUTE_CONTROL_TYPES.DROPDOWN:
        case ATTRIBUTE_CONTROL_TYPES.LIST:
        case ATTRIBUTE_CONTROL_TYPES.TREE:
            return ['single', 'multiply'];

        default:
            if (isOfTimePartControlType(controlType)) {
                return ['single', 'multiply'];
            }

            return ['single'];
    }
}

export function getDefaultOptions(
    controlType: string,
    defaultAttributeType: string,
    selectionType?: AttributeSelectionType,
    relation?: string,
    options?: IAttributeOptions
): IAttributeOptions {
    let result: IAttributeOptions;

    if (relation != null) {
        const validRelations = getValidRelations(defaultAttributeType, selectionType);

        if (validRelations != null && validRelations.length > 0) {
            const rel = validRelations.indexOf(relation) >= 0 ? relation : validRelations[0];

            result = {
                relation: rel
            };
        }
    }

    if (isOfEntityValuesControlType(controlType)) {
        if (result == null) {
            result = {};
        }

        result.rows = 5;
        result.showingType = 'all';
        result.parentAttribute = null;

        if (options != null) {
            result = $.extend(true, {}, options);
        }
    }

    return result;
}

export function normalizeAttribute(
    value: IAttribute,
    dataSourceID: number,
    glob: IGlob,
    controlAndAttributeTypes?: IControlAndAttributeTypes
): void {
    const attribute = value.attribute;

    if (attribute == null && value.expression == null) {
        delete value.options;
        delete value.defaultValue;
        return;
    }

    if (value.expression != null) {
        delete value.valueType;
    }

    if (controlAndAttributeTypes == null) {
        controlAndAttributeTypes = getControlAndAttributeTypes(value, dataSourceID, glob);
    }

    const { attributeType, defaultAttributeType, controlType } = controlAndAttributeTypes;
    const noValue = attributeCannotHaveValue(value, attributeType);

    let options = value.options;
    let valueType = value.valueType;

    if (noValue) {
        if (options == null) {
            value.options = options = {};
        }

        options.isHidden = true;

        delete options.variableName;
        delete options.defaultValueDataQuery;

        if (value.valueType != null) {
            const relation = options != null ? options.relation : null;

            if (filterValueTypeCannotBeChanged(attributeType, relation)) {
                valueType = 'constant';
                delete value.valueType;
            }
        }
    }

    // let validControlTypes: ITypeNameElement[];

    // switch (valueType) {
    //     case 'variable':
    //         validControlTypes = getValidControlTypes(ATTRIBUTE_TYPES.VARIABLE, relation, false);
    //         break;

    //     case 'timePart':
    //         delete value.controlType;

    //         validControlTypes = [{
    //             type: attribute,
    //             name: attribute
    //         }];
    //         break;

    //     default:
    //         validControlTypes = getValidControlTypes(attributeType, relation, false);
    // }

    // let controlType = controlAndAttributeTypes.controlType;

    // Fix Control Type.
    // if (validControlTypes != null) {
    //     if (
    //         controlType == null ||
    //         _.find(validControlTypes, (validType) => {
    //             return validType.type === controlType;
    //         }) == null
    //     ) {
    //         controlType = validControlTypes[0].type;
    //         value.controlType = controlType;
    //     }
    // } else {
    //     delete value.controlType;
    // }

    if (controlType != null) {
        value.controlType = controlType;
    } else {
        delete value.controlType;
    }

    setIntervalsByControlType(value, attribute, valueType, controlType, dataSourceID, glob);

    // Fix Selection Type.
    const validSelectionTypes = getValidSelectionTypes(controlType);

    if (validSelectionTypes.indexOf(value.selectionType) === -1) {
        value.selectionType = validSelectionTypes[0];
    }

    // Fix Options.
    if (options == null) {
        value.options = options = getDefaultOptions(controlType, defaultAttributeType, value.selectionType);
    } else {
        const relationValues = getValidRelations(defaultAttributeType, value.selectionType);

        if (relationValues != null && relationValues.indexOf(options.relation) === -1) {
            options.relation = relationValues[0];
        }

        normalizeDataQueryValue(options.dataQuery, controlType, valueType);

        if (_.isEmpty(options.dataQuery)) {
            delete options.dataQuery;
        }

        normalizeDataQueryValue(options.defaultValueDataQuery, controlType, valueType);

        if (_.isEmpty(options.defaultValueDataQuery)) {
            delete options.defaultValueDataQuery;
        }
    }

    // Fix Default Value;
    if (
        (Array.isArray(value.defaultValue) && value.defaultValue.length === 0) ||
        (options != null && options.defaultValueDataQuery != null)
    ) {
        delete value.defaultValue;
    }

    switch (attributeType) {
        case ATTRIBUTE_TYPES.INTEGER:
        case ATTRIBUTE_TYPES.REAL:
            if (value.allowNull === false && value.defaultValue == null) {
                let timePart: string;

                switch (valueType) {
                    case 'variable':
                        timePart = controlType;
                        break;

                    case 'timePart':
                        timePart = attribute;
                        break;

                    default:
                        break;
                }

                if (isOfTimePartControlType(timePart)) {
                    if (isFullYearTimePart(timePart)) {
                        value.defaultValue = [
                            {
                                value: new Date().getFullYear().toString()
                            }
                        ];
                    } else {
                        value.defaultValue = [
                            {
                                value: '1'
                            }
                        ];
                    }
                }
            }
            break;

        case ATTRIBUTE_TYPES.LOGICAL:
            if (value.defaultValue == null) {
                value.defaultValue = [
                    {
                        value: 'true'
                    }
                ];
            }

            value.allowNull = false;
            break;

        case ATTRIBUTE_TYPES.ANYTYPE:
            delete value.defaultValue;
            break;

        default:
            break;
    }

    if (value.defaultValue != null) {
        value.defaultValue = correctFilterValue(
            value.defaultValue,
            value.selectionType,
            controlType,
            attributeType,
            options
        );
    }

    if (value.defaultValue == null || (Array.isArray(value.defaultValue) && value.defaultValue.length === 0)) {
        delete value.defaultValue;
    }
}

export function buildAttributeFilterWidgetFilterCollection(
    resetFilters: boolean,
    widgetParameters: IAttributeFilterWidgetParameters,
    globalState: GlobalState,
    staticWidgetID: number,
    scopeOptions: IScopeOptions,
    workspaceID: number,
    theme: IValuesCollection,
    cultureValues: ICultureValues,
    glob: IGlob,
    beforeResolveCallback: (filterCollections: INewAttributeFilterColections) => void
): JQueryPromise<INewAttributeFilterColections> {
    if (widgetParameters == null) {
        return $.Deferred().reject();
    }

    // VR: clean NotApplied filters if ApplyMode=OFF:
    if (!applyModeIsOn(globalState, theme)) {
        globalState.dropNotAppliedFilters(); //WorkItem [104528]: NotApplied filter still exists in the state when User turned OFF the Apply Mode
    }

    const dataQueries = widgetParameters.attributes;

    if (dataQueries == null) {
        return $.Deferred().reject();
    }

    const result = globalState.getAttributeFilterWidgetDeferred(staticWidgetID);

    if (result.inProcess) {
        return result.deferred;
    } else {
        result.inProcess = true;
    }

    const dataSources = dataQueries.map((dataQuery) => dataQuery.dataSourceID);

    glob.EntityModelsStore.loadModels(dataSources).done(() => {
        const resultCollection: IGlobalAttributeFilterCollection = {};
        const dataGettingDeferreds: JQueryDeferred<any>[] = [];
        const timeZone = DateTimeFunctions.getTimeZoneFromTheme(theme);

        let ownFiltersCollection: IGlobalAttributeFilterCollection;
        let existingFiltersCollection = globalState.getShelvedAttributeFilterCollection();
        let removedFiltersCollection: IGlobalAttributeFilterCollection;

        const filtersAreApplied = existingFiltersCollection == null;

        if (filtersAreApplied) {
            existingFiltersCollection = globalState.getAttributeFilterCollection();
        }

        if (existingFiltersCollection != null && !resetFilters) {
            for (let dataSourceID in existingFiltersCollection) {
                const existingFilters = existingFiltersCollection[dataSourceID];

                if (existingFilters == null) {
                    continue;
                }

                let ownFiltersCopy: IGlobalAttributeFilters = null;

                for (let attributeID in existingFilters) {
                    const existingFilter = existingFilters[attributeID];

                    if (
                        existingFilter.widgetID === staticWidgetID ||
                        (existingFilter.widgetID == null && existingFilter.clickToFilterAttribute)
                    ) {
                        if (ownFiltersCopy == null) {
                            ownFiltersCopy = {};
                        }

                        const filterCopy = $.extend(true, {}, existingFilter);
                        const attributeID = createAttributeID(existingFilter, dataSourceID, ownFiltersCopy);

                        delete filterCopy.removed;

                        // ID must be renewed because below the old values are get by ID in a local collection, but here ID is from the global one.
                        ownFiltersCopy[attributeID] = filterCopy;
                    }
                }

                if (ownFiltersCopy != null) {
                    if (ownFiltersCollection == null) {
                        ownFiltersCollection = {};
                    }

                    ownFiltersCollection[dataSourceID] = ownFiltersCopy;
                }
            }
        }

        dataQueries.forEach((dataQuery) => {
            const { dataSourceID } = dataQuery;
            const dataSourceIDStr = dataSourceID.toString();
            const oldGlobalFilters = ownFiltersCollection != null ? ownFiltersCollection[dataSourceIDStr] : null;

            let resultGlobalFilters = resultCollection[dataSourceIDStr];

            if (resultGlobalFilters == null) {
                resultCollection[dataSourceIDStr] = resultGlobalFilters = {};
            }

            dataQuery.attributes.forEach((attribute) => {
                const attributeCopy = $.extend(true, {}, attribute);
                const { controlType } = attributeCopy;
                const attributeID = createAttributeID(attributeCopy, dataSourceID, resultGlobalFilters);
                const controlAndAttributeTypes = getControlAndAttributeTypes(attributeCopy, dataSourceID, glob);

                normalizeAttribute(attributeCopy, dataSourceID, glob, controlAndAttributeTypes);

                if (isFiscalTimePart(controlType)) {
                    if (attributeCopy.intervals.timePeriodStartMonth == null) {
                        if ((attributeCopy.intervals as any).fiscalTimePartStartMonth == null) {
                            //EVG 01.06.2020: Obsolete. Drop it.
                            const themeTimePartStartMonth = theme[themeVars.fiscalFirstMonth];

                            attributeCopy.intervals.timePeriodStartMonth =
                                themeTimePartStartMonth != null ? parseInt(themeTimePartStartMonth, 10) : 1;
                        }
                    }
                }

                let values: IDisplayValue[];

                if (oldGlobalFilters != null) {
                    const oldFilter = oldGlobalFilters[attributeID];

                    if (oldFilter != null) {
                        const oldValues = oldFilter.values;
                        const numberOfOldValues = Array.isArray(oldValues) ? oldValues.length : 0;

                        if (!filtersAreApplied || numberOfOldValues > 0) {
                            if (attribute.selectionType !== 'single' || numberOfOldValues === 1) {
                                if (!oldFilter.removed && oldFilter.clickToFilterAttribute) {
                                    if (
                                        oldFilter.controlType == null ||
                                        oldFilter.controlType === attribute.controlType
                                    ) {
                                        if (removedFiltersCollection == null) {
                                            removedFiltersCollection = {};
                                        }

                                        let removedFilters = removedFiltersCollection[dataSourceIDStr];

                                        if (removedFilters == null) {
                                            removedFilters = removedFiltersCollection[dataSourceIDStr] = {};
                                        }

                                        oldFilter.removed = true;
                                        removedFilters[oldFilter.id] = oldFilter;
                                        values = oldFilter.values;
                                    }
                                } else {
                                    values = oldFilter.values;
                                }
                            }
                        }
                    }
                }

                const attributeOptions = attributeCopy.options;
                const allowNull = attributeCopy.allowNull !== false;
                const newFilter: IGlobalAttributeFilter = {
                    id: attributeID,
                    widgetID: staticWidgetID,
                    ...attributeCopy
                };

                values = correctFilterValue(
                    values,
                    attributeCopy.selectionType,
                    controlType,
                    controlAndAttributeTypes.attributeType,
                    attributeOptions
                );

                const defaultValueDataQuery = attributeOptions?.defaultValueDataQuery;

                // Default Value must not be set for not applied values except when reset is called.
                // Saved filter collection may have no value for a filter that in new configuration can have nulls not allowed.
                // In this case, Default Value is applied to fix it.
                if ((values == null || (values.length === 0 && !allowNull)) && (filtersAreApplied || resetFilters)) {
                    if (defaultValueDataQuery != null) {
                        const dataGettingDeferred = $.Deferred();
                        const filter = globalState.makeLocalFilter(
                            defaultValueDataQuery.filter,
                            dataSourceID,
                            null,
                            theme,
                            null,
                            cultureValues,
                            null
                        );
                        const { dataQueryData, keySelect } = getDataQueryData(
                            attributeCopy,
                            null,
                            workspaceID,
                            staticWidgetID,
                            defaultValueDataQuery
                        );

                        // It is possible to specify Data Query Filter only without other attributes.
                        if (
                            filter.length > 0 &&
                            dataQueryData.attribute == null &&
                            dataQueryData.keyAttribute == null &&
                            dataQueryData.expression == null
                        ) {
                            dataQueryData.attribute = attributeCopy.attribute;
                            dataQueryData.expression = attributeCopy.expression;
                        }

                        if (dataQueryData.expression != null) {
                            dataQueryData.expression = globalState.expressionBindVariables(
                                dataQueryData.expression,
                                dataSourceID,
                                null,
                                scopeOptions
                            );
                        }

                        if (dataQueryData.keyAttribute?.expression != null) {
                            dataQueryData.keyAttribute.expression = globalState.expressionBindVariables(
                                dataQueryData.keyAttribute.expression,
                                dataSourceID,
                                null,
                                scopeOptions
                            );
                        }

                        if (attributeCopy.selectionType === 'single') {
                            dataQueryData.limit = 1;
                        }

                        dataQueryData.loadKeyAttribute = keySelect;

                        const loadValidValuesResult = glob.EntityModelsStore.loadEntityValues(
                            dataSourceID,
                            theme,
                            dataQueryData,
                            filter
                        );

                        loadValidValuesResult.promise
                            .done((enitityValues) => {
                                values = correctFilterValue(
                                    enitityValues.displayValues,
                                    attributeCopy.selectionType,
                                    controlType,
                                    controlAndAttributeTypes.attributeType,
                                    attributeOptions
                                );

                                if (values == null || values.length === 0) {
                                    glob.AppActions.showNotification_Warning(
                                        `The Data Query for Default Value of ${
                                            dataQueryData.attribute ?? dataQueryData.expression?.text ?? 'a field of'
                                        } Attribute Filter has returned no value or null. The filter is not applied.`,
                                        null,
                                        { manualDestroy: true }
                                    );
                                }

                                newFilter.values = values ?? [];
                            })
                            .always(() => {
                                dataGettingDeferred.resolve();
                            });

                        dataGettingDeferreds.push(dataGettingDeferred);
                    } else {
                        values = attributeCopy.defaultValue;

                        if ((values == null || values.length === 0) && !allowNull) {
                            // Default Value for Boolean fileds can be absent in configuration even if nulls are not allowed.
                            // In this case, value is set to false.
                            if (controlAndAttributeTypes.attributeType === ATTRIBUTE_TYPES.BOOLEAN) {
                                values = [
                                    {
                                        value: 'false'
                                    }
                                ];
                            }
                        }
                    }
                }

                if (
                    defaultValueDataQuery == null &&
                    attributeOptions?.selectFirstNodeByDefault &&
                    (values == null || values.length === 0)
                ) {
                    const { dataQueryData, attributeDataQuery } = getDataQueryData(
                        attributeCopy,
                        null,
                        workspaceID,
                        staticWidgetID
                    );

                    if (
                        controlType === ATTRIBUTE_CONTROL_TYPES.DROPDOWN ||
                        controlType === ATTRIBUTE_CONTROL_TYPES.LIST
                    ) {
                        const dataGettingDeferred = $.Deferred();

                        dataQueryData.limit = 1;

                        if (dataQueryData.filters == null) {
                            dataQueryData.filters = [];
                        }

                        const notNullFilter: IQueryFilter = {
                            relation: RELATIONS.IS_NOT_NULL
                        };
                        const { attribute, keyAttribute } = dataQueryData;

                        if (keyAttribute != null) {
                            notNullFilter.attribute = keyAttribute.attribute;
                        } else if (dataQueryData.loadKeyAttribute) {
                            const entityKeyAttribute = glob.EntityModelsStore.getKeyAttribute(attribute, dataSourceID);

                            if (entityKeyAttribute != null) {
                                notNullFilter.attribute = entityKeyAttribute;
                            }
                        } else {
                            notNullFilter.attribute = attribute;
                        }

                        dataQueryData.filters.push(notNullFilter);

                        getEntityValuesFilteredByGlobalFilter(
                            dataSourceID,
                            dataQueryData,
                            {
                                showingType: attributeOptions?.showingType ?? 'all',
                                currentFilter: newFilter,
                                globalState: globalState,
                                scopeOptions: scopeOptions,
                                cultureValues: cultureValues
                            },
                            undefined,
                            attributeOptions?.sortingType,
                            false,
                            theme,
                            glob
                        )
                            .done((responce) => {
                                newFilter.values = values = responce.displayValues ?? [];
                                dataGettingDeferred.resolve();
                            })
                            .always(() => {
                                dataGettingDeferred.resolve();
                            });

                        dataGettingDeferreds.push(dataGettingDeferred);
                    } else if (controlType === ATTRIBUTE_CONTROL_TYPES.TREE) {
                        const dataGettingDeferred = $.Deferred();
                        const treeSelectAttrbuteData = getDataQueryDataForTree(
                            dataQueryData,
                            attributeDataQuery,
                            attributeOptions
                        );

                        getTreeValuesFilteredByGlobalFilter(
                            dataSourceID,
                            treeSelectAttrbuteData,
                            {
                                showingType: attributeOptions?.showingType ?? 'all',
                                currentFilter: newFilter,
                                globalState: globalState,
                                scopeOptions: scopeOptions,
                                cultureValues: cultureValues
                            },
                            undefined,
                            attributeOptions?.sortingType,
                            true,
                            false,
                            theme,
                            glob
                        )
                            .done((responce) => {
                                newFilter.values = values = getFirstTreeItem(responce, true);
                                dataGettingDeferred.resolve();
                            })
                            .always(() => {
                                dataGettingDeferred.resolve();
                            });

                        dataGettingDeferreds.push(dataGettingDeferred);
                    }
                }

                newFilter.values = values ?? [];

                if (scopeOptions != null) {
                    newFilter.scopeOptions = scopeOptions;
                }

                if (
                    (attributeCopy.label == null || attributeCopy.label === '') &&
                    attributeCopy.valueType === 'variable'
                ) {
                    newFilter.label = attributeCopy.attribute;
                }

                if (DateTimeFunctions.isTypeOfDatetime(attributeCopy.controlType)) {
                    newFilter.timeZone = timeZone;
                }

                resultGlobalFilters[attributeID] = newFilter;
            });
        });

        const resolve = () => {
            const resultCollections = {
                newCollection: resultCollection,
                toRemove: removedFiltersCollection
            };

            if (beforeResolveCallback != null) {
                beforeResolveCallback(resultCollections);
            }

            result.inProcess = false;
            result.deferred.resolve(resultCollections);
        };

        if (dataGettingDeferreds.length === 0) {
            resolve();
        } else {
            $.when(...dataGettingDeferreds).always(() => {
                resolve();
            });
        }
    });

    return result.deferred.promise();
}

// 'all' - Local Filter only is applied.
// 'valid' - Local Filter + Global Filter are applied.
// 'suggesting' - Local Filter is always applied. Full filter is applied to get suggestions.
export function getEntityValuesFilteredByGlobalFilter(
    dataSourceID: number,
    attributeData: IDataQueryData,
    globalFilter: IGlobalFilterProperties,
    localFilter: IQueryFilter[],
    sortingType: AttributeValueSortingType,
    ignoreBackendCache: boolean,
    theme: IValuesCollection,
    glob: IGlob,
    cleanPreviousEntityValuesCache?: (valuesType: string, newCollectionKey: string) => void,
    setIgnoreBackendCache?: (value: boolean) => void
): GetValuesResult {
    const result: GetValuesResult = $.Deferred();
    const { globalState, currentFilter, showingType, cultureValues, scopeOptions } = globalFilter;
    const suggestingShowingType = showingType === 'suggesting';

    // 'fullFilter' gets filters from Global State. When any of the filters is changed then values for the both queires must be reloaded.
    // The query getting valid values uses 'fullFilter', but the main query does it sometimes.
    // It is why the first query directs the second one with 'ignoreLocalCache'.
    let { aqlFilter: validValuesFilter, actualLocalFilter } = globalState.buildFilterToGetValidValuesForAttribute(
        dataSourceID,
        currentFilter,
        localFilter,
        attributeData,
        showingType,
        theme,
        cultureValues,
        scopeOptions
    );

    const validValuesFilterIsSet = validValuesFilter != null && validValuesFilter.length > 0;
    const loadValidValues = suggestingShowingType && validValuesFilterIsSet;
    const validValuesDeffered = $.Deferred<IDisplayValue[]>();

    let ignoreLocalCache = false;

    applyWidgetCulture(theme, () => {
        if (loadValidValues) {
            const loadValidValuesResult = glob.EntityModelsStore.loadEntityValues(
                dataSourceID,
                theme,
                attributeData,
                validValuesFilter,
                sortingType,
                ignoreBackendCache
            );

            loadValidValuesResult.promise
                .done((validValuesEnitity) => {
                    if (!loadValidValuesResult.dataFromCache && cleanPreviousEntityValuesCache != null) {
                        ignoreLocalCache = true;
                        cleanPreviousEntityValuesCache('validValues', loadValidValuesResult.collectionKey);
                    }

                    validValuesDeffered.resolve(validValuesEnitity.displayValues);
                })
                .fail(() => {
                    validValuesDeffered.reject();
                });
        } else {
            validValuesDeffered.resolve([]);
        }
    });

    validValuesDeffered
        .done((validValues) => {
            const mainValuesResult = glob.EntityModelsStore.loadEntityValues(
                dataSourceID,
                theme,
                attributeData,
                suggestingShowingType ? actualLocalFilter : validValuesFilter,
                sortingType,
                ignoreBackendCache,
                ignoreLocalCache
            );

            mainValuesResult.promise
                .done((entity) => {
                    if (!mainValuesResult.dataFromCache && cleanPreviousEntityValuesCache != null) {
                        cleanPreviousEntityValuesCache('mainValues', mainValuesResult.collectionKey);
                    }

                    if (loadValidValues) {
                        if (validValues.length > 0) {
                            const reorderedValues: IDisplayValue[] = [];
                            const reoderedValidValues: IDisplayValue[] = [];

                            validValues.forEach((validValue) => {
                                reorderedValues.push(validValue);

                                if (suggestingShowingType) {
                                    reoderedValidValues.push(validValue);
                                }
                            });

                            if (suggestingShowingType) {
                                entity.displayValues?.forEach((entityValue) => {
                                    if (
                                        reoderedValidValues.find((selectedValue) => {
                                            return (
                                                selectedValue.value === entityValue.value &&
                                                selectedValue.displayValue === entityValue.displayValue
                                            );
                                        }) == null
                                    ) {
                                        reorderedValues.push(entityValue);
                                    }
                                });
                            }

                            result.resolve({
                                displayValues: reorderedValues,
                                validValues: reoderedValidValues
                            });
                        } else {
                            // No valid value is found.
                            result.resolve({
                                displayValues: entity.displayValues,
                                validValues: []
                            });
                        }
                    } else {
                        result.resolve({
                            displayValues: entity.displayValues
                        });
                    }

                    if (ignoreBackendCache && setIgnoreBackendCache != null) {
                        setIgnoreBackendCache(ignoreBackendCache);
                    }
                })
                .fail(() => {
                    result.reject();
                });
        })
        .fail(() => {
            result.reject();
        });

    return result;
}

export function getTreeValuesFilteredByGlobalFilter(
    dataSourceID: number,
    attributeData: ITreeDataQueryData,
    globalFilter: IGlobalFilterProperties,
    localFilter: IQueryFilter[],
    sortingType: AttributeValueSortingType,
    loadAll: boolean,
    ignoreBackendCache: boolean,
    theme: IValuesCollection,
    glob: IGlob
) {
    const result = $.Deferred<ITreeValues>();

    applyWidgetCulture(theme, () => {
        glob.EntityModelsStore.loadModels([dataSourceID])
            .done(() => {
                let aqlFilter: IAQLFilter[];

                if (loadAll !== true) {
                    aqlFilter = globalFilter.globalState.buildFilterToGetValidValuesForAttribute(
                        dataSourceID,
                        globalFilter.currentFilter,
                        localFilter,
                        attributeData,
                        globalFilter.showingType,
                        theme,
                        globalFilter.cultureValues,
                        globalFilter.scopeOptions
                    ).aqlFilter;
                }

                let parentAttribute = attributeData.parentAttribute;

                if (attributeData.attribute != null && parentAttribute == null) {
                    parentAttribute = glob.EntityModelsStore.getDefaultParentAttribute(
                        attributeData.attribute,
                        dataSourceID
                    );
                }

                glob.EntityModelsStore.loadTreeEntityValues(
                    dataSourceID,
                    attributeData.attribute,
                    parentAttribute,
                    attributeData,
                    theme,
                    attributeData.rootNodeID,
                    attributeData.labelAttribute,
                    aqlFilter,
                    attributeData.limit,
                    sortingType,
                    ignoreBackendCache
                )
                    .done((values) => {
                        result.resolve(values);
                    })
                    .fail(() => {
                        result.reject();
                    });
            })
            .fail(() => {
                result.reject();
            });
    });

    return result;
}

export function getFirstTreeItem(treeValues: ITreeValues, selectFirstNodeByDefault: boolean): IDisplayValue[] {
    if (selectFirstNodeByDefault && treeValues != null && treeValues.tree != null && treeValues.tree.length > 0) {
        return [
            {
                value: treeValues.tree[0].id,
                displayValue: treeValues.tree[0].title
            }
        ];
    }

    return [];
}
